import React, { useState } from "react";

import { apiRequest } from "@mhj/shared/api";
import { Card, Field, StatusPill, buttonClass, inputClass } from "../lib/ui";

type Result = {
  applicationNo: string;
  status: string;
  track: string | null;
  submittedAt: string | null;
};

/**
 * Status without an account.
 *
 * Essential for walk-in applicants an officer registered, who may never sign
 * in — and it cuts down "what is happening with my application" phone calls.
 */
export default function PublicStatus() {
  const [applicationNo, setApplicationNo] = useState("");
  const [phone, setPhone] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const data = await apiRequest<Result>("/empowerment/status", {
        params: { applicationNo: applicationNo.trim(), phone: phone.trim() },
      });
      setResult(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card
        title="Check your application"
        description="Enter the ID we sent you by text message, and the phone number you applied with."
      >
        <form className="space-y-4" onSubmit={check}>
          <Field label="Application ID" required>
            <input
              className={inputClass}
              value={applicationNo}
              onChange={(e) => setApplicationNo(e.target.value)}
              placeholder="JIE/2026/004821"
              autoComplete="off"
            />
          </Field>
          <Field label="Phone number" required>
            <input
              className={inputClass}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              inputMode="tel"
              placeholder="08031234567"
            />
          </Field>
          <button type="submit" className={buttonClass} disabled={busy}>
            {busy ? "Checking…" : "Check status"}
          </button>
        </form>
      </Card>

      {error && (
        <Card>
          <p className="text-sm text-red-700">{error}</p>
          <p className="mt-2 text-xs text-slate-500">
            Check both the ID and the number. The ID looks like JIE/2026/004821.
          </p>
        </Card>
      )}

      {result && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Application</p>
              <p className="text-xl font-bold text-slate-900">{result.applicationNo}</p>
            </div>
            <StatusPill status={result.status} />
          </div>
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            {result.track && (
              <div>
                <dt className="text-slate-500">Applied for</dt>
                <dd className="font-medium text-slate-900">{result.track}</dd>
              </div>
            )}
            {result.submittedAt && (
              <div>
                <dt className="text-slate-500">Submitted</dt>
                <dd className="font-medium text-slate-900">
                  {new Date(result.submittedAt).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </Card>
      )}
    </div>
  );
}
